import classNames from "classnames";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useFloating, autoUpdate, flip, shift } from "@floating-ui/react-dom";

export const Dropdown = ({
  widthClasses = "w-56",
  modal = true,
  closeOnClick = false,
  ...props
}) => {
  const dropRef = useRef();
  const menuButtonRef = useRef(null);
  const navigate = useNavigate();
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [
      flip({ allowedPlacements: ["bottom-start", "bottom-end"] }),
      shift(),
    ],
  });

  const [hasBeenInit, setHasBeenInit] = useState(false);

  // Support programmatic auto-open even when ref timing is finicky.
  // This is important for flows like "Add Filter" where we want the newly-added
  // filter pill to open its options immediately without an extra click.
  useEffect(() => {
    if (!props.defaultOpen) return;
    if (hasBeenInit) return;
    if (!menuButtonRef.current) return;

    setHasBeenInit(true);
    // HeadlessUI's Menu opens on click; trigger it after mount.
    menuButtonRef.current.click();
  }, [props.defaultOpen, hasBeenInit]);

  function handleClick(item, close) {
    props.itemClick && props.itemClick(item);
    if (item.onClick) {
      item.onClick(item);
      if (closeOnClick) close?.();
      return;
    }
    // Many dropdowns (ex: table filters) provide items without `path`.
    // Avoid navigating to an undefined route which can interrupt UI flows.
    if (item.path) {
      navigate(item.path);
    }
    if (closeOnClick) close?.();
  }

  function renderItem(item, i, close) {
    const title = item.title || item.label;
    return (
      <MenuItem as="div" key={title + i}>
        {({ focus }) => {
          const itemClasses = classNames(
            props.itemClasses,
            "group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer",
            {
              "bg-primary-500 text-white": focus,
              "text-gray-900": !focus,
              "!text-gray-600 !bg-none !cursor-not-allowed": item.disabled,
            }
          );
          return (
            <div
              onClick={item.disabled ? null : () => handleClick(item, close)}
              className={itemClasses}
            >
              <span className="mr-1.5">{item.icon}</span>
              {typeof title === "function"
                ? title({ item, active: focus, focus })
                : title}
            </div>
          );
        }}
      </MenuItem>
    );
  }

  const wrapperClasses = classNames(
    "relative h-full w-full inline-block text-left",
    props.className
  );

  const dataWrapperClasses = classNames(
    "absolute mt-2 rounded-md z-[1001]",
    "bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
    widthClasses,
    {
      "origin-top-right right-0": props.align === "right" || !props.align,
      "origin-top-left left-0": props.align === "left",
    },
    props.dataWrapperClassName
  );

  const trigger = React.Children.map([props.trigger], (child, index) => {
    const childProps = { ...child.props, className: "" }; //<Menu.Button {...child.props}>{}</Menu.Button>;
    let as;

    if (typeof child.type === "function") {
      as = React.Fragment;
    }

    return (
      <MenuButton
        ref={(el) => {
          if (props.defaultOpen && !hasBeenInit) {
            setHasBeenInit(true);
            el?.click();
          }
          menuButtonRef.current = el;
          refs.setReference(el);
        }}
        // ref={refs.setReference}
        as="div"
        {...childProps}
      >
        {child}
      </MenuButton>
    );
  });

  function renderData(dropData, close) {
    return Array.isArray(dropData) ? (
      <div key="2" className="py-1 w-full" role="none">
        {dropData.map((item, i) => renderItem(item, i, close))}
      </div>
    ) : typeof dropData === "function" ? (
      dropData(close)
    ) : (
      dropData
    );
  }

  return (
    <Menu as="div" className={wrapperClasses}>
      {({ open, close }) => {
        return props.openOnHover ? (
          <div
            onMouseEnter={() => {
              if (!open) {
                menuButtonRef.current?.click();
              }
            }}
            onMouseLeave={() => {
              if (open) {
                close();
              }
            }}
          >
            {trigger}
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                ref={refs.setFloating}
                as="div"
                modal={modal}
                className={dataWrapperClasses}
                style={floatingStyles}
              >
                <div ref={dropRef} className="px-1 py-1">
                  {props.data && renderData(props.data, close)}
                </div>
              </MenuItems>
            </Transition>
          </div>
        ) : (
          <>
            {trigger}
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                ref={refs.setFloating}
                as="div"
                modal={modal}
                className={dataWrapperClasses}
                style={floatingStyles}
              >
                <div ref={dropRef} className="px-1 py-1">
                  {props.data && renderData(props.data, close)}
                </div>
              </MenuItems>
            </Transition>
          </>
        );
      }}
    </Menu>
  );
};

export default Dropdown;
